import React, { useState, useEffect } from 'react';
import { checkRateLimit, updateRateLimit, RATE_LIMIT_KEYS } from '../utils/rateLimiter';
import { trackFormSubmission, trackError } from '../utils/analytics';
import './ContactForm.css';

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [formError, setFormError] = useState('');

  // Check and update rate limit timer
  useEffect(() => {
    const timer = setInterval(() => {
      const { remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.CONTACT_FORM);
      setRemainingTime(remainingTime);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = e => {
    e.preventDefault();
    setFormError('');

    // Check rate limit
    const { allowed, remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.CONTACT_FORM);
    if (!allowed) {
      setFormError(`Please wait ${remainingTime} seconds before submitting again.`);
      trackError('validation', 'Rate limit exceeded', 'ContactForm');
      return;
    }

    // Get form data
    const formData = new FormData(e.target);

    // Basic form validation
    const email = formData.get('email');
    const message = formData.get('message');

    if (message.length < 10) {
      setFormError('Message must be at least 10 characters long.');
      trackError('validation', 'Message too short', 'ContactForm');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setFormError('Please enter a valid email address.');
      trackError('validation', 'Invalid email format', 'ContactForm');
      return;
    }

    // Update rate limit timestamp
    updateRateLimit(RATE_LIMIT_KEYS.CONTACT_FORM);

    // Track successful form submission
    trackFormSubmission('contact', true);

    // Submit the form
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData).toString(),
    })
      .then(() => setSubmitted(true))
      .catch(() => setFormError('Failed to send message. Please try again later.'));
  };

  if (submitted) {
    return (
      <div className="full-page-wrapper" style={{ background: 'linear-gradient(to left bottom, #777777, #1a1a1a)', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <div className="app-theme-contact-form" style={{ paddingTop: 'var(--spacing-lg, 30px)' }}>
          <div className="success-message">
            <h3>Thank you for your message!</h3>
            <p>We'll get back to you soon.</p>
            <button onClick={() => setSubmitted(false)} disabled={remainingTime > 0}>
              {remainingTime > 0
                ? `Wait ${remainingTime}s to send another message`
                : 'Send another message'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="full-page-wrapper" style={{ background: 'linear-gradient(to left bottom, #777777, #1a1a1a)', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      <div className="app-theme-contact-form" style={{ paddingTop: 'var(--spacing-lg, 30px)' }}>
        <form
          name="contact"
          method="POST"
          netlify="true"
          netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="contact-form-container"
        >
          <input type="hidden" name="form-name" value="contact" />
          <p hidden>
            <label>
              Don't fill this out if you're human: <input name="bot-field" />
            </label>
          </p>

          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input type="text" id="name" name="name" required />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input type="email" id="email" name="email" required />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message:</label>
            <textarea id="message" name="message" required></textarea>
          </div>

          <div data-netlify-recaptcha="true"></div>

          <button
            type="submit"
            disabled={remainingTime > 0}
            className={remainingTime > 0 ? 'disabled' : ''}
          >
            {remainingTime > 0 ? `Wait ${remainingTime}s` : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;
